import {css} from '@linaria/core';
import {styled} from '@linaria/react';
import React from 'react';
import {COLORS} from '../../../constants/colors';
import starFilledIcon from './assets/ic-star-filled.svg?inline';
import starOutlinedIcon from './assets/ic-star-outline.svg?inline';

export type size = 'S' | 'M' | 'L';
export interface StarRatingProps {
  value: number;
  starsColor?: string;
  outlineColor?: keyof typeof COLORS;
  size?: size;
  dataTestid?: string;
  className?: string;
}

export function StarRating({
  value,
  starsColor,
  size = 'S',
  dataTestid = 'starRating',
  outlineColor,
  className,
}: StarRatingProps) {
  return (
    <Wrapper
      className={className}
      data-testid={dataTestid}
      starsColor={starsColor}
      size={size}
      outlineColor={outlineColor}
    >
      <EmptyStars
        className={sharedStyles}
        style={{maskImage: `url("${starOutlinedIcon}")`}}
      />
      <FilledStars
        className={sharedStyles}
        $value={value}
        style={{maskImage: `url("${starFilledIcon}")`}}
      />
    </Wrapper>
  );
}

const baseWidth = 60;
const baseHeight = 12;
const baseMaskSize = 12;

const sizesMap: {
  [k in size]: {
    width: number;
    height: number;
    maskSize: number;
  };
} = {
  L: {
    width: baseWidth * 1.8,
    height: baseHeight * 1.8,
    maskSize: baseMaskSize * 1.8,
  },
  M: {
    width: baseWidth * 1.4,
    height: baseHeight * 1.4,
    maskSize: baseMaskSize * 1.4,
  },
  S: {
    width: baseWidth,
    height: baseHeight,
    maskSize: baseMaskSize,
  },
};

const sharedStyles = css`
  height: 100%;
  left: 0;
  mask-repeat: repeat-x;
  position: absolute;
  top: 0;
`;

const EmptyStars = styled.div`
  height: 100%;
  left: 0;
  mask-repeat: repeat-x;
  position: absolute;
  top: 0;
  width: 100%;
`;

const FilledStars = styled.div<{$value: number}>`
  height: 100%;
  left: 0;
  mask-repeat: repeat-x;
  position: absolute;
  top: 0;
  width: ${({$value}) => `${$value * 20}%`};
`;

const Wrapper = styled.div<
  Pick<StarRatingProps, 'starsColor' | 'outlineColor'> & {size: size}
>`
  height: ${({size}) => `${sizesMap[size].height}px`};
  margin-right: 0.5rem;
  position: relative;
  width: ${({size}) => `${sizesMap[size].width}px`};

  ${EmptyStars} {
    background: ${({outlineColor}) =>
      outlineColor ? COLORS[outlineColor] : COLORS.Black};
    mask-size: ${({size}) => `${sizesMap[size].maskSize}px`};
  }

  ${FilledStars} {
    background: ${({starsColor}) => starsColor || COLORS.Black};
    mask-size: ${({size}) => `${sizesMap[size].maskSize}px`};
  }
`;
